/*
  Auto gift: every product that has the product metafield custom.gift_product (type "Product")
  gets that gift added to the cart automatically (custom.gift_quantity per unit bought, default 1; line property _auto_gift).
  The gift quantity follows the trigger quantity and the gift is removed when no product that
  gives it is left in the cart.
  The $0 price comes from the Shopify automatic "Buy X get Y" discount.

  It wraps window.fetch for /cart/add and /cart/change, so every add-to-cart path of the theme
  (product page, quick add, cards, drawer) behaves the same, and merges the refreshed
  `sections` into the theme's response so the drawer already shows the gift.
  The mapping product -> gift comes from the `auto-gift-map` section (Section Rendering API).
*/
(function () {
  if (window.__autoGiftInstalled) return;
  window.__autoGiftInstalled = true;

  var origFetch = window.fetch.bind(window);
  var GIFT_PROP = '_auto_gift';
  var DISMISS_KEY = 'auto_gift_dismissed';

  function urlOf(input) {
    if (typeof input === 'string') return input;
    return (input && input.url) || '';
  }
  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  }
  function isCartChange(url) {
    return /\/cart\/change(\.js)?(\?|$)/.test(url);
  }
  function jsonHeaders() {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  // `sections` / `sections_url` of the theme's own request, to reuse them in our requests
  function sectionsParams(body) {
    var out = {};
    try {
      if (!body) return out;
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (body.get('sections')) out.sections = String(body.get('sections'));
        if (body.get('sections_url')) out.sections_url = String(body.get('sections_url'));
      } else if (typeof body === 'string') {
        if (body.charAt(0) === '{') {
          var json = JSON.parse(body);
          if (json.sections) out.sections = json.sections;
          if (json.sections_url) out.sections_url = json.sections_url;
        } else {
          var params = new URLSearchParams(body);
          if (params.get('sections')) out.sections = params.get('sections');
          if (params.get('sections_url')) out.sections_url = params.get('sections_url');
        }
      }
    } catch (e) {}
    return out;
  }

  // Gifts the customer removed on purpose: don't add them back while their trigger stays in the cart
  function getDismissed() {
    try {
      return JSON.parse(window.localStorage.getItem(DISMISS_KEY) || '[]').map(String);
    } catch (e) {
      return [];
    }
  }
  function setDismissed(list) {
    try {
      if (list.length) window.localStorage.setItem(DISMISS_KEY, JSON.stringify(list));
      else window.localStorage.removeItem(DISMISS_KEY);
    } catch (e) {}
  }
  function addDismissed(variantId) {
    var list = getDismissed();
    variantId = String(variantId);
    if (list.indexOf(variantId) === -1) {
      list.push(variantId);
      setDismissed(list);
    }
  }

  function getCart() {
    return origFetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (r) {
      return r.json();
    });
  }

  // Lines of the cart with the gift that corresponds to each one
  function getMap() {
    return origFetch('/?sections=auto-gift-map&_=' + Date.now(), { cache: 'no-store' })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        var doc = new DOMParser().parseFromString(sections['auto-gift-map'] || '', 'text/html');
        var script = doc.getElementById('auto-gift-data');
        return script ? JSON.parse(script.textContent) : [];
      })
      .catch(function () {
        return null;
      });
  }

  function withSections(payload, sp) {
    if (sp.sections) payload.sections = sp.sections;
    if (sp.sections_url) payload.sections_url = sp.sections_url;
    return payload;
  }

  // Runs ONE correction (add missing gifts or remove the excess) and resolves with its response,
  // or null when the cart is already right. Shopify re-splits/merges discounted lines after every
  // change (line keys change), so each correction is computed from a fresh read of the cart.
  function reconcilePass(sp) {
    return getMap().then(function (lines) {
      if (!lines) return null;

      // gift variant -> units wanted (one gift per unit of the products that give it)
      var desired = {};
      lines.forEach(function (line) {
        if (line.is_gift || !line.gift_variant_id) return;
        var id = String(line.gift_variant_id);
        desired[id] = (desired[id] || 0) + (Number(line.quantity) || 1) * (Number(line.gift_quantity) || 1);
      });

      // forget dismissals of gifts that are no longer wanted
      setDismissed(getDismissed().filter(function (id) { return desired[id]; }));
      var dismissed = getDismissed();

      // gift lines already in the cart, by variant
      var giftLines = {};
      lines.forEach(function (line) {
        if (!line.is_gift) return;
        var id = String(line.variant_id);
        (giftLines[id] = giftLines[id] || []).push(line);
      });

      // Shopify may split a discounted line in several lines, so compare totals per gift variant
      var steps = [];
      Object.keys(desired).forEach(function (variantId) {
        var lines = giftLines[variantId] || [];
        var total = lines.reduce(function (sum, line) { return sum + (Number(line.quantity) || 0); }, 0);
        if (!lines.length && dismissed.indexOf(variantId) !== -1) return;
        if (total >= desired[variantId]) return;
        var properties = {};
        properties[GIFT_PROP] = '1';
        steps.push({
          url: '/cart/add.js',
          payload: { items: [{ id: Number(variantId), quantity: desired[variantId] - total, properties: properties }] }
        });
      });
      // remove the excess of gifts (0 wanted removes orphan gifts), starting from the last line
      Object.keys(giftLines).forEach(function (variantId) {
        var lines = giftLines[variantId];
        var total = lines.reduce(function (sum, line) { return sum + (Number(line.quantity) || 0); }, 0);
        var excess = total - (desired[variantId] || 0);
        for (var i = lines.length - 1; i >= 0 && excess > 0; i--) {
          var take = Math.min(Number(lines[i].quantity) || 0, excess);
          excess -= take;
          steps.push({ url: '/cart/change.js', payload: { id: lines[i].key, quantity: (Number(lines[i].quantity) || 0) - take } });
        }
      });
      if (!steps.length) return null;

      var step = steps[0];
      return origFetch(step.url, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(withSections(step.payload, sp))
      }).then(function (response) {
        return response.ok ? response : null;
      });
    }).catch(function () {
      return null;
    });
  }

  // Adds missing gifts and removes orphan/excess ones, one correction at a time until the cart is
  // right. Resolves with the last response (the one with refreshed sections) or null if unchanged.
  function reconcile(sp) {
    var last = null;
    var passes = 0;
    function next() {
      return reconcilePass(sp).then(function (response) {
        if (!response) return last;
        last = response;
        if (++passes >= 10) return last;
        return next();
      });
    }
    return next();
  }

  // Original theme response + sections refreshed after our changes
  function mergeResponses(original, refreshed, isChange) {
    if (!refreshed) return Promise.resolve(original);
    return Promise.all([original.clone().json(), refreshed.clone().json(), isChange ? getCart() : null])
      .then(function (data) {
        // change.js responds with the whole cart (re-read it, it changed); add.js with the added item(s)
        var base = isChange ? data[2] : data[0];
        if (data[1].sections) base.sections = data[1].sections;
        return new Response(JSON.stringify(base), {
          status: original.status,
          statusText: original.statusText,
          headers: { 'Content-Type': 'application/json' }
        });
      })
      .catch(function () {
        return original;
      });
  }

  // Detect when the customer removes a gift line (quantity 0) so it isn't added back
  function noteGiftRemoval(body) {
    try {
      if (typeof body !== 'string' || body.charAt(0) !== '{') return Promise.resolve();
      var req = JSON.parse(body);
      if (Number(req.quantity) !== 0) return Promise.resolve();
      if (req.id) {
        addDismissed(String(req.id).split(':')[0]);
        return Promise.resolve();
      }
      if (req.line) {
        return getCart().then(function (cart) {
          var item = cart.items[Number(req.line) - 1];
          if (item) addDismissed(item.variant_id);
        });
      }
    } catch (e) {}
    return Promise.resolve();
  }

  // The gift line follows the units of the product that gives it. When the customer raises the
  // quantity of the gift line itself, the extra units are bought as a normal (paid) line instead:
  // the gift line keeps its quantity and a separate line without the gift property is added.
  // Resolves with the (possibly rewritten) request body.
  function splitGiftIncrease(body) {
    try {
      if (typeof body !== 'string' || body.charAt(0) !== '{') return Promise.resolve(body);
      var req = JSON.parse(body);
      var quantity = Number(req.quantity);
      if (!(quantity > 0) || (!req.id && !req.line)) return Promise.resolve(body);
      return getCart().then(function (cart) {
        var item = null;
        if (req.line) item = cart.items[Number(req.line) - 1];
        else {
          cart.items.forEach(function (line) {
            if (!item && (line.key === String(req.id) || String(line.id) === String(req.id))) item = line;
          });
        }
        if (!item || !item.properties || !item.properties[GIFT_PROP] || quantity <= item.quantity) return body;
        var extra = quantity - item.quantity;
        return origFetch('/cart/add.js', {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ items: [{ id: item.variant_id, quantity: extra }] })
        }).then(function (response) {
          if (!response.ok) return body;
          // Shopify may have re-split the lines (new keys), so the original line key can be stale.
          // Answer the theme with a harmless "no change" on the first line (returns the whole cart).
          return getCart().then(function (fresh) {
            if (!fresh.items.length) return body;
            var noop = { line: 1, quantity: fresh.items[0].quantity };
            if (req.sections) noop.sections = req.sections;
            if (req.sections_url) noop.sections_url = req.sections_url;
            return JSON.stringify(noop);
          });
        });
      }).catch(function () {
        return body;
      });
    } catch (e) {
      return Promise.resolve(body);
    }
  }

  // Refresh drawer + counters after changing the cart outside a theme request (page load)
  function refreshUI() {
    if (/^\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    origFetch('/?sections=minicart-form')
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        var el = document.getElementById('minicart-form');
        var doc = new DOMParser().parseFromString(sections['minicart-form'], 'text/html');
        var fresh = doc.querySelector('#minicart-form');
        if (el && fresh) el.innerHTML = fresh.innerHTML;
        if (window.BlsLazyloadImg && BlsLazyloadImg.init) BlsLazyloadImg.init();
      })
      .catch(function () {});
    getCart()
      .then(function (cart) {
        document.querySelectorAll('.cart-count').forEach(function (el) {
          if (el.classList.contains('cart-count-drawer')) el.innerHTML = '(' + cart.item_count + ')';
          else el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
        });
        var total = document.querySelector('header-total-price');
        if (total && total.updateTotal) total.updateTotal(cart);
        var bar = document.querySelector('free-ship-progress-bar');
        if (bar && bar.init) bar.init(cart.items_subtotal_price);
      })
      .catch(function () {});
  }

  window.fetch = function (input, init) {
    var url = urlOf(input);
    var body = init && init.body;

    if (isCartAdd(url)) {
      var sp = sectionsParams(body);
      return origFetch(input, init).then(function (response) {
        if (!response.ok) return response;
        return reconcile(sp).then(function (refreshed) {
          return mergeResponses(response, refreshed, false);
        });
      });
    }

    if (isCartChange(url)) {
      var spc = sectionsParams(body);
      return splitGiftIncrease(body).then(function (newBody) {
        if (newBody !== body) {
          init = Object.assign({}, init, { body: newBody });
          body = newBody;
        }
        return noteGiftRemoval(body);
      }).then(function () {
        return origFetch(input, init).then(function (response) {
          if (!response.ok) return response;
          return reconcile(spc).then(function (refreshed) {
            return mergeResponses(response, refreshed, true);
          });
        });
      });
    }

    return origFetch(input, init);
  };

  // "Comprar ahora" (Shopify dynamic checkout button) goes straight to checkout with only that
  // variant and skips the cart, so the gift would never be added. For products that give a gift,
  // add to the cart (the fetch wrapper adds the gift) and then go to checkout.
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('.btn-checkout-dynamic[data-has-gift] button');
      if (!button) return;
      var form = button.closest('form');
      if (!form || button.disabled || button.closest('.disabled')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.__giftBusy) return;
      button.__giftBusy = true;
      // same feedback as the native button: it just gets disabled until the page changes
      button.disabled = true;
      var data = new FormData(form);
      data.delete('sections');
      data.delete('sections_url');
      window
        .fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          window.location.href = '/checkout';
        })
        .catch(function () {
          button.__giftBusy = false;
          button.disabled = false;
          form.submit();
        });
    },
    true
  );

  // On every page load: add missing gifts (trigger already in cart) or clean up orphan gifts
  reconcile({}).then(function (changed) {
    if (changed) refreshUI();
  });
})();
